import { StrictMode } from "react";
import type { Root } from "react-dom/client";
import { ContractClient } from "../src/contract-client.js";
import { createCuekardsApi } from "../src/cuekards-api.js";
import {
  attachSettingsPersistence,
  settingsStorage,
} from "../src/settings-bridge.js";
import { attachTakeDelivery } from "../src/delivery-bridge.js";
import { SPEECH_FRAME, sendSpeechLink } from "../src/speech-link.js";
import { deliverTake } from "./deliver.js";
import { attachModelDownload, modelUnavailable } from "./model-download.js";
import { askThisBrowserToPersist } from "./persist.js";
import { browserDelivery } from "./save.js";
import { mirroredStorage } from "./settings-mirror.js";
import { setupOverride, thisPagesSetup } from "./settings-url.js";
import { setTakeNotice } from "./take-notice.js";
import { WebApp } from "./WebApp.js";

/**
 * Everything the host does: the two workers, `window.cuekards`, and the interface.
 *
 * A module of its own rather than the entry point's body, and the reason is the
 * capability gate in front of it (web ticket 17). **None of this may happen on a
 * browser that is about to be turned away** — not the workers, not the permission
 * prompt, and not the download panel offering sixty megabytes — and the only way
 * for a module's top level to be conditional is for it not to be a top level.
 * `main.tsx` decides which of the two screens this browser gets; this is one of
 * them.
 *
 * The order things happen in here is load-bearing, and each step says why.
 */
export function boot(root: Root): void {
  /**
   * The core worker, holding every controller and the settings store.
   *
   * A module worker, bundled to a file of its own beside this one.
   */
  const worker = new Worker(new URL("./core-worker.js", import.meta.url), {
    type: "module",
  });

  /**
   * The recognition worker, holding the recogniser and the follower (web ticket 14).
   *
   * Separate from the core worker so that a decode spike cannot delay a countdown
   * tick or a stop command; ADR-0011 is why the follower controller went with it.
   *
   * A **classic** worker, unlike the core one. The WebAssembly loader is CommonJS
   * and is brought in with `importScripts`, which is what keeps the page's content
   * security policy from having to allow arbitrary strings to be evaluated — see
   * `recognition/sherpa-runtime.ts`.
   */
  const recogniser = new Worker(new URL("./recogniser-worker.js", import.meta.url));

  /**
   * A deployment served without the recogniser, said out loud once.
   *
   * The worker's script is only built when there is a WebAssembly artifact to load, so
   * a site built or deployed without one has nothing at this URL. That is a supported
   * state and not a broken one — no follower, `speechUnavailable` never lowered, and a
   * working conventional teleprompter with an explanation on screen — but it is worth a
   * line in the console, because from the outside it is indistinguishable from a
   * recogniser that is merely slow.
   */
  recogniser.onerror = () => {
    console.warn(
      "cuekards: this deployment serves no speech recogniser — the prompter works in manual and constant-speed modes",
    );
    // And on screen, because the download panel is otherwise waiting for a worker
    // that will never report. This is the one thing about the model that the page
    // knows and the worker cannot: its own script was not there to run.
    modelUnavailable("this deployment serves no speech recogniser.");
  };

  /**
   * The first-visit download, page side (web ticket 15).
   *
   * Attached before the interface is mounted, and outside it, so the status the
   * worker reports on startup is not missed by a panel that had not rendered yet —
   * and so a download survives stepping into the settings.
   *
   * `askThisBrowserToPersist` is handed in rather than called first and forgotten,
   * and that is what makes the "persistent storage is requested before caching"
   * rule structural rather than a matter of ordering: `persist()` is a window API,
   * so the page is the only context that can ask, and making the page the thing
   * that sends `start` means there is no way to begin caching without having asked.
   */
  attachModelDownload({ port: recogniser, persist: askThisBrowserToPersist });

  /**
   * Settings, and the rig this page's address asks for (web ticket 18).
   *
   * Before anything else is posted: the worker's settings store reads through the
   * snapshot this sends, and a port delivers in order.
   *
   * The override is what makes a bookmarked setup work, and its second half is
   * what keeps it from being a booby trap. A setup is **in force without being
   * saved**: it is applied over what this browser has stored on the way in, and
   * every write that comes back out has the values it owns put back the way this
   * browser had them on the way to storage. A link is somebody else's as often as
   * your own, and one that silently rewrote the recipient's saved rig would be a
   * change they could neither see nor undo. Keeping a setup is what importing a
   * settings file is for, and that is a deliberate act.
   *
   * `settingsStorage` is this browser's, or one that forgets: reaching for
   * `window.localStorage` in a tab with site data blocked throws on the property
   * access itself. Without storage cuekards runs and the settings are this
   * session's only — which the settings screen says. `mirroredStorage` is the
   * page keeping its own copy current as the worker writes, so the export and
   * the shareable link are of the settings as they are rather than as they were
   * when a screen mounted.
   */
  attachSettingsPersistence(
    worker,
    mirroredStorage(settingsStorage()),
    setupOverride(thisPagesSetup()),
  );

  /**
   * Introduce the two workers to each other.
   *
   * The page is the only context that can: a port has to be *transferred*, and only
   * whoever created the channel has one to give. What travels down it is not audio
   * and not positions on demand — it is the follower controller's own ports, which
   * were messages in everything but name already. See `src/speech-link.ts`.
   *
   * **After the settings snapshot, deliberately.** The first thing the core worker
   * does with a link is tell the follower which microphone to open, and the answer to
   * that is a stored setting — so a link handled before the snapshot would push the
   * system default over the device the user chose last time.
   *
   * Before the first frame can be posted, which a port's ordering guarantees for
   * free — and before the interface is mounted, so a script loaded on arrival from a
   * shared link reaches a follower that is already listening.
   */
  const link = new MessageChannel();
  sendSpeechLink(recogniser, link.port1);
  sendSpeechLink(worker, link.port2);

  /**
   * Ask the browser not to evict the footage (web ticket 13).
   *
   * At startup rather than when a take begins, because a permission call is not
   * something to make from the transport — and because what it protects is
   * whatever is already in storage, which is a take that has not been recovered
   * yet. Nothing waits on the answer: a refusal means the data is evictable under
   * disk pressure, not that recording is unavailable, and there is nothing on
   * screen for it to change. `persist` is a *window* API, which is why this is
   * here and its sibling `estimate` is in the worker.
   *
   * The model download asks again before it caches anything (web ticket 15). Not a
   * duplicate call in practice — `askForPersistentStorage` checks `persisted()`
   * first, and by then this one has settled — but the two are asking for different
   * things to be kept, and neither should depend on the other having happened.
   */
  void askThisBrowserToPersist();

  /**
   * Getting a finished take out of the browser — the one thing the worker asks the
   * page for rather than the other way round.
   *
   * Attached here, once, outside the component tree, and that placement is
   * load-bearing rather than tidy: a request has exactly one answer, and a handler
   * that came and went with a mount would answer a stop twice and open two save
   * dialogs over one take.
   */
  const delivery = browserDelivery();
  attachTakeDelivery(worker, async (request) => {
    const outcome = await deliverTake(delivery, request);
    setTakeNotice(outcome.ok ? outcome.explanation : undefined);
    return outcome.ok
      ? { ok: true, output: outcome.output }
      : { ok: false, message: outcome.message };
  });

  const client = new ContractClient(worker);
  window.cuekards = createCuekardsApi(client, {
    /**
     * The microphone fork, going the short way (FR-31).
     *
     * The one call on the contract that does not reach the core worker. Frames
     * arrive eight times a second from the audio worklet, and the core worker is
     * where a take's bytes are being written to storage synchronously — putting the
     * fork in that queue is precisely the delay the second worker exists to avoid.
     * The buffer is transferred rather than copied, which is safe because the worklet
     * hands the page a fresh copy of every frame.
     */
    sendSpeechFrame: (samples) => {
      recogniser.postMessage({ kind: SPEECH_FRAME, samples }, [samples.buffer]);
    },
  });

  root.render(
    <StrictMode>
      <WebApp />
    </StrictMode>,
  );
}
